import React, { useEffect, useState, useRef } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CtaButton } from "@/components/ui/CtaButton";
import { GlowField } from "@/components/ui/GlowField";
import { AppLogo } from "@/components/ui/AppLogo";
import LiveSharedOverlay from "@/components/live/LiveSharedOverlay";
import UnifiedWhiteboard from "@/pages/UnifiedWhiteboard";
import { ArrowRight, Loader2, ShieldCheck, Video, ExternalLink, Copy, Link2 } from "lucide-react";

/**
 * Guest class joiner — public route reached via a shareable invite link.
 * Format: /guest/:classId?token=<guest_token>
 *
 * Flow:
 *  1. Fetch GET /api/public/class/:classId?token=... to verify token + show class info
 *  2. Guest types display name, knocks, waits for the teacher to admit
 *  3. Once admitted → class hub: the Google Meet video opens in a NEW TAB,
 *     while quizzes / slides / the Interactive Board arrive right here.
 */
export default function GuestClass() {
  const { classId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const token = params.get("token") || "";
  const direct = params.get("direct") === "1"; // live-board link from the Meet chat — no waiting room
  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [name, setName] = useState(params.get("name") || user?.full_name || "");
  const [joining, setJoining] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [admitted, setAdmitted] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  // ─── Live-broadcast reception for guests ─────────────────────────────────
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [wbCollab, setWbCollab] = useState(null); // board draw permission set by teacher
  const [wbView, setWbViewState] = useState(null); // mirrored board viewport
  const [participantId, setParticipantId] = useState(""); // stable guest id from presence
  const [dismissedOverlayId, setDismissedOverlayId] = useState("");
  const [classEnded, setClassEnded] = useState(false);
  const guestToken = token;

  useEffect(() => {
    if (!admitted || !classId || !guestToken) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/class/${classId}/live-state`, { params: { token: guestToken } });
        if (!alive) return;
        setActiveOverlay(data?.state?.active_overlay || null);
        setWbCollab(data?.state?.wb_collab || null);
        setWbViewState(data?.state?.wb_view || null);
        if (data?.state?.class_status === "completed") setClassEnded(true);
      } catch { /* network blip — try again next tick */ }
    };
    poll();
    const t = setInterval(poll, 2500);
    return () => { alive = false; clearInterval(t); };
  }, [admitted, classId, guestToken]);

  // Presence heartbeat — makes this guest visible in the teacher's
  // share-target modal and returns our stable participant_id.
  useEffect(() => {
    if (!admitted || !classId || !guestToken) return;
    const beat = async () => {
      try {
        const { data } = await api.post(`/public/class/${classId}/presence`, { token: guestToken, name });
        if (data?.participant_id) setParticipantId(data.participant_id);
      } catch { /* retry next beat */ }
    };
    beat();
    const t = setInterval(beat, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admitted, classId, guestToken]);

  useEffect(() => {
    (async () => {
      if (!token) { setErr("This invite link is missing a token."); setLoading(false); return; }
      try {
        const { data } = await api.get(`/public/class/${classId}`, { params: { token } });
        setCls(data.class);
        if (data.class?.status === "completed") setClassEnded(true);
        // If the visitor is the class teacher, punt them into the full
        // authenticated Live Classroom so they get all controls.
        if (user?.role === "teacher" && user?.id === data.class?.teacher_id) {
          nav(`/live/${classId}`, { replace: true });
          return;
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Invite link is invalid or expired.");
      } finally { setLoading(false); }
    })();
  }, [classId, token, user?.id, user?.role, nav]);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name to join");
    setJoining(true);
    if (direct) {
      // Teacher shared this link deliberately (Meet chat) — straight in.
      setAdmitted(true);
      return;
    }
    try {
      // Knock first — wait in the waiting room until the teacher admits us.
      const { data: k } = await api.post(`/public/class/${classId}/knock`, { token, name: name.trim() });
      setWaiting(true);
      const entryId = k.entry.id;
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/public/class/${classId}/knock/${entryId}`, { params: { token } });
          if (st.status === "admitted") {
            setWaiting(false);
            setAdmitted(true);
            return;
          }
          if (st.status === "denied") {
            setWaiting(false); setJoining(false);
            toast.error("The teacher didn't admit you this time.");
            return;
          }
        } catch { /* keep polling */ }
        pollRef.current = setTimeout(poll, 3000);
      };
      poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reach the classroom");
      setJoining(false);
    }
  };

  const meetUrl = cls?.meet_url || cls?.meeting_link || "";
  const openMeet = () => {
    if (!meetUrl) return toast.error("The teacher hasn't attached a video link yet");
    window.open(meetUrl, "_blank", "noopener,noreferrer");
  };
  const copyMeet = async () => {
    if (!meetUrl) return;
    try { await navigator.clipboard.writeText(meetUrl); toast.success("Google Meet link copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FBFAF7] flex items-center justify-center text-neutral-500">
      <Loader2 className="animate-spin mr-2" size={18} /> Verifying invite…
    </div>
  );
  if (waiting) return (
    <div className="min-h-screen bg-[#05070C] flex items-center justify-center p-6" data-testid="guest-waiting-room">
      <div className="max-w-md w-full text-center bg-[#0B1220] border border-[#22D3EE]/25 rounded-2xl p-8 shadow-[0_0_50px_rgba(34,211,238,0.15)]">
        <div className="mx-auto w-14 h-14 rounded-full border-2 border-[#22D3EE]/50 border-t-transparent animate-spin" />
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#22D3EE] mt-5">/ waiting room</div>
        <h1 className="font-display font-bold text-2xl text-white mt-2">Hang tight, {name || "there"} — your teacher will let you in.</h1>
        <p className="text-sm text-neutral-400 mt-2">{cls?.title} · You'll join automatically once admitted.</p>
      </div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen bg-[#FBFAF7] flex flex-col items-center justify-center p-6 text-center">
      <AppLogo size="lg" to="/" />
      <div className="mt-8 max-w-md bg-white border border-black/8 rounded-2xl p-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-red-500 mb-2">/ invalid link</div>
        <h1 className="font-display font-bold text-2xl">Hmm, this link won&apos;t work.</h1>
        <p className="text-sm text-neutral-600 mt-2">{err}</p>
        <p className="text-xs text-neutral-500 mt-4">Ask your teacher to share a fresh link.</p>
      </div>
    </div>
  );

  // Class over — every shared screen (board, quizzes, Meet) is finished.
  if (classEnded) return (
    <div className="min-h-screen bg-[#05070C] flex items-center justify-center p-6" data-testid="guest-class-ended">
      <div className="max-w-md w-full text-center bg-[#0B1220] border border-white/10 rounded-2xl p-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">/ class ended</div>
        <h1 className="font-display font-bold text-2xl text-white mt-3">This class has ended.</h1>
        <p className="text-sm text-neutral-400 mt-2">{cls?.title} · Thanks for joining! Your teacher has disconnected — the board and shared activities are now closed.</p>
      </div>
    </div>
  );

  // Once admitted — guest class hub: Meet in a new tab, activities right here.
  if (admitted) {
    return (
      <div className="h-[100dvh] max-h-[100dvh] bg-[#0A0C10] flex flex-col overflow-hidden" data-testid="guest-live">
        {/* Top bar */}
        <div className="shrink-0 min-h-14 flex items-center flex-wrap gap-2 px-3 sm:px-4 py-2 border-b border-white/10 bg-[#10141C] text-white text-xs">
          <AppLogo size="sm" to="/" />
          <span className="ml-2 sm:ml-4 font-mono uppercase tracking-[0.2em] text-neutral-500 truncate max-w-[140px] sm:max-w-[220px]">
            GUEST · {cls?.title}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {meetUrl && !direct && (
              <button
                onClick={openMeet}
                data-testid="guest-open-meet"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full bg-[#00897B] text-white hover:bg-[#00A48F] transition-colors"
              >
                <Video size={13} /> Open Meet <ExternalLink size={11} />
              </button>
            )}
            <span className="text-neutral-300 pl-2 border-l border-white/10">{name}</span>
          </div>
        </div>

        {/* Meet hub — hidden for board-link visitors who are already in the call */}
        <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center p-6" data-testid="guest-meet-hub">
          {direct ? (
            <div className="w-full max-w-xl bg-[#0B1220] border border-[#22D3EE]/30 rounded-2xl p-6 sm:p-10 text-center" data-testid="guest-board-hub">
              <div className="mx-auto w-12 h-12 rounded-full border-2 border-[#22D3EE]/50 border-t-transparent animate-spin" />
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#22D3EE] mt-5">/ live board · connected</div>
              <h2 className="font-display font-bold text-2xl text-white tracking-tight mt-3">{cls?.title}</h2>
              <p className="text-sm text-neutral-400 mt-3 leading-relaxed">
                You&apos;re in, {name}! Keep your <strong className="text-neutral-200">Google Meet call</strong> running in its own tab —
                the Interactive Board, quizzes and slides your teacher shares will appear <strong className="text-neutral-200">right here</strong> automatically.
              </p>
            </div>
          ) : (
          <div className="w-full max-w-xl bg-[#0B1220] border border-[#00A48F]/30 rounded-2xl p-6 sm:p-10">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-[#2DD4BF]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              / live class · google meet
            </div>
            <h2 className="font-display font-bold text-2xl text-white tracking-tight mt-3">{cls?.title}</h2>
            <p className="text-sm text-neutral-400 mt-1">{cls?.subject}</p>
            {meetUrl ? (
              <>
                <button
                  onClick={openMeet}
                  data-testid="guest-join-meet-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl bg-[#00897B] hover:bg-[#00A48F] text-white font-bold text-base transition-all hover:shadow-[0_0_30px_rgba(0,164,143,0.5)]"
                >
                  <Video size={18} /> Join Google Meet <ExternalLink size={14} />
                </button>
                <div className="mt-3 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                  <Link2 size={13} className="text-neutral-500 shrink-0" />
                  <span className="text-xs font-mono text-neutral-300 truncate flex-1">{meetUrl}</span>
                  <button onClick={copyMeet} data-testid="guest-copy-meet"
                    className="text-[11px] font-bold px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/25 text-white shrink-0 inline-flex items-center gap-1">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-6 bg-white/5 border border-dashed border-white/15 rounded-xl p-6 text-center text-sm text-neutral-300" data-testid="guest-no-meet">
                The teacher hasn't attached a video link yet — quizzes and the board still arrive right here.
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-5 leading-relaxed">
              The video opens in a <strong className="text-neutral-300">new tab</strong>. Keep this tab open — quizzes, slides and the Interactive Board your teacher shares appear here automatically.
            </p>
          </div>
          )}
        </div>

        {/* Live broadcast overlay reception — quizzes, polls, slides, images. */}
        {activeOverlay && activeOverlay.type !== "whiteboard" && activeOverlay.id !== dismissedOverlayId && (
          <LiveSharedOverlay
            overlay={activeOverlay}
            classId={classId}
            isTeacher={false}
            guestToken={guestToken}
            guestName={name}
            myParticipantId={participantId}
            myName={name}
            onStudentClose={() => setDismissedOverlayId(activeOverlay.id)}
            onDismiss={() => { /* only teacher can dismiss */ }}
          />
        )}
        {/* Interactive Board — direct board-link guests get the board IMMEDIATELY
            (the shared link itself is the access grant); invite guests get it
            when the teacher shares it. Drawing needs Remote Access. */}
        {(direct || activeOverlay?.type === "whiteboard") && (() => {
          const canDraw = !!wbCollab?.enabled && ((wbCollab?.allowed_ids || []).includes("*") || (!!participantId && (wbCollab?.allowed_ids || []).includes(participantId)));
          return (
            <div className="fixed inset-0 top-14 z-[60] bg-white flex flex-col" data-testid="guest-remote-board">
              <UnifiedWhiteboard
                embedded
                classId={classId}
                isTeacher={false}
                defaultCollab={true}
                readOnly={!canDraw}
                forcedTheme={activeOverlay?.payload?.theme || null}
                followView={wbView}
                guest={{ token: guestToken, name }}
                onViewBroadcast={canDraw
                  ? (v) => api.post(`/public/class/${classId}/wb-view`, { ...v, token: guestToken, guest_name: name }).catch(() => {})
                  : undefined}
              />
            </div>
          );
        })()}
      </div>
    );
  }

  // Otherwise — show the guest entry form (no sign-up required)
  return (
    <div className="min-h-screen bg-[#FBFAF7] flex flex-col">
      <div className="px-6 py-5 border-b border-black/8 bg-white/70 backdrop-blur"><AppLogo size="md" to="/" /></div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-black/8 rounded-2xl p-8" data-testid="guest-form">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-2">{direct ? "/ live board access" : "/ guest invite"}</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">{direct ? "Open the live board" : "Join the class"}</h1>
          <div className="mt-3 text-sm text-neutral-600">
            <span className="font-semibold text-[#0A0C10]">{cls?.title}</span>
            {cls?.subject ? <> · {cls.subject}</> : null}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{fmtDateTime(cls?.start_time)} · {cls?.duration_minutes} min</div>

          <form onSubmit={join} className="mt-6 space-y-3">
            <GlowField
              label="Your name"
              testid="guest-name"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="So your teacher recognises you"
            />
            <CtaButton type="submit" variant="primary" size="lg" disabled={joining} data-testid="guest-join" className="w-full rounded-full">
              {joining ? "Preparing room…" : direct ? "Open board" : "Join class"} <ArrowRight size={16} />
            </CtaButton>
          </form>
          {direct && (
            <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed" data-testid="guest-direct-note">
              Already in the Google Meet call? Perfect — this tab is only for the live Interactive Board, no need to join the Meet again.
            </p>
          )}
          <div className="mt-5 text-[11px] text-neutral-500 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> No sign-up required · Video runs on Google Meet
          </div>
        </div>
      </div>
    </div>
  );
}
